import { MotionIcon } from 'motion-icons-react';
import Card from '../../../components/base/Card';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useCommunity } from '@/context/CommunityContext';

/**
 * AdminSection component displaying server administrators and team information.
 *
 * @description Shows the network's core leadership (Lytair, Greiton, HekuT)
 * sourced from the shared community data — filtered to the core tier with a
 * platform or KF1 scope. Each profile shows role, badge, description, and
 * specialties. Includes a help section for users needing assistance.
 *
 * @returns {JSX.Element} The admin section component
 */
export default function AdminSection() {
  const sectionRef = useScrollReveal();
  const { leadership } = useCommunity();
  /**
   * Core leadership shown on the KF1 site: the network's core tier, scoped to
   * the platform or to KF1 (Lytair, Greiton, HekuT). Sourced from the shared
   * community data so the roster stays in sync across all sites.
   */
  const admins = leadership.filter(
    (m) => m.tier === 'core' && (m.scope === 'platform' || m.scope === 'kf1')
  );

  return (
    <section id="team" ref={sectionRef} className="py-20 bg-black/80">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-orbitron horror-heading">
            Meet Our <span className="text-red-500">Team</span>
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Our shared Uliunai leadership keeps the platform running, alongside the admins who look after the
            Killing Floor server day to day.
          </p>
          <a
            href="https://uliunai.com/#team"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-sm font-semibold"
          >
            Meet the full Uliunai team
            <i className="ri-arrow-right-s-line"></i>
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {admins.map((admin, index) => {
            const initials = admin.initials ?? admin.name.slice(0, 2).toUpperCase();
            const specialties = admin.specialties ?? [];
            return (
            <div key={admin.name} className="scroll-reveal" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
            <Card variant="blood" className="text-center hover:scale-[1.02] transition-transform duration-300 h-full flex flex-col">
              <div className="relative mb-6">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-red-600 shadow-lg shadow-red-600/25 bg-gray-800 flex items-center justify-center">
                  {admin.avatar ? (
                    <img
                      src={admin.avatar}
                      alt={admin.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Swap a broken/missing avatar for an initials fallback.
                        const img = e.currentTarget;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement | null;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span
                    className="w-full h-full items-center justify-center font-orbitron text-3xl font-bold text-red-300"
                    style={{ display: admin.avatar ? 'none' : 'flex' }}
                  >
                    {initials}
                  </span>
                </div>
                {admin.badge && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {admin.badge}
                  </div>
                )}
              </div>

              <h3 className="text-2xl font-bold text-white mb-2 font-orbitron">{admin.name}</h3>
              <p className="text-red-400 font-semibold mb-4">{admin.role}</p>

              {admin.description && (
                <p className="text-gray-300 mb-6 leading-relaxed flex-1">{admin.description}</p>
              )}

              {specialties.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-white mb-3 font-orbitron">Specialties</h4>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="bg-gray-800 text-red-400 px-3 py-1 rounded-full text-sm border border-red-900/30"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            </Card>
            </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <Card variant="dark" className="inline-block">
            <div className="flex items-center gap-4">
              <MotionIcon name="Headphones" color="#f87171" size={30} animation="bounce"  />
              <div>
                <h3 className="text-xl font-bold text-white font-orbitron">Need Help?</h3>
                <p className="text-gray-300">Our admins are available 24/7 to assist you</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
